/**
 * The settings control (D172): one gear in the control row that discloses
 * the session's preferences — and the export — together.
 *
 * @remarks
 * **Why one disclosure and not three toggles.** D130 folded the mute switch
 * and the volume lever behind one speaker because the row has to fit a
 * phone. Then the clock (D153) and the account (D157) joined the row, and a
 * 390px phone had Map, Journal, Voices, Images, Sound, Clock and Sign in to
 * seat in 319px: the row scrolled, and the visually-hidden labels of the
 * buttons pushed past its end widened the page itself (Mike, 2026-09-11).
 * The row now carries the three references, this gear, and the account —
 * five buttons, which fit — and everything a player SETS rather than reads
 * sits behind the gear: the plate, the sound and its level, the clock. The
 * export sits under a rule at the end: an action rather than a setting, but
 * a once-a-session one that had been hidden from phones outright, and the
 * panel is the one place in the shell with room for it (Mike's ruling).
 *
 * The panel is what the sound disclosure was — `position: fixed`, anchored
 * to the trigger by measurement, closed by Escape, an outside press or a
 * resize — with the row's controls as its children. The children are the
 * shell's: it alone knows which layers the pack declares. The gear carries a
 * small struck-speaker mark while sound is off, so the one state the old
 * speaker showed at a glance is still shown (Mike's ruling).
 *
 * @packageDocumentation
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';

/** Props for {@link SettingsControlComponent}. */
export interface SettingsControlComponentProps {
	/** The panel's rows — the preference toggles, the slider, the export — in the shell's order. */
	readonly children: ReactNode;
	/** Whether the ambient layer is muted, for the mark on the gear; absent, the pack has no sound. */
	readonly isSoundMuted?: boolean | undefined;
}

/** Where the panel hangs: measured from the trigger, as offsets from the viewport's bottom and right. */
interface PanelAnchor {
	readonly bottom: number;
	readonly right: number;
}

/**
 * Render the settings control: the gear, and the panel it discloses.
 *
 * @param props - {@link SettingsControlComponentProps}
 * @returns The control
 */
export const SettingsControlComponent = ({ children, isSoundMuted = false }: SettingsControlComponentProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [panelAnchor, setPanelAnchor] = useState<PanelAnchor>({ bottom: 0, right: 0 });
	const containerElementRef = useRef<HTMLDivElement>(null);
	const triggerElementRef = useRef<HTMLButtonElement>(null);

	/*
	 * The panel is `position: fixed`, anchored to the trigger by measurement,
	 * because the control row it sits in is a SCROLL CONTAINER
	 * (`overflow-x: auto`, which is also what lets the row shrink below its
	 * min-content). A scroll container clips its descendants, so an absolutely
	 * positioned panel was cropped to the row's own height — present in the DOM,
	 * invisible on screen, and with the transcript above it swallowing the
	 * clicks. Fixed positioning is measured against the viewport instead, which
	 * no ancestor here can clip: nothing between this and the document root
	 * carries a `transform`, `filter` or `contain` that would make it behave
	 * like an absolute one.
	 *
	 * Measured on open rather than tracked continuously: the row does not move
	 * while the panel is up — the page itself never scrolls (only the transcript
	 * does) — so a resize is the only thing that could stale this, and a resize
	 * with the panel open closes it below.
	 */
	// Measured from the element the click ARRIVED on rather than from the ref:
	// `currentTarget` is the trigger by construction — it is what was pressed —
	// so there is no absence to guard against, where reading the ref types as
	// possibly-null and needs a branch that a pressed button can never take.
	const openPanel = (trigger: HTMLButtonElement): void => {
		const triggerRect = trigger.getBoundingClientRect();

		setPanelAnchor({ bottom: window.innerHeight - triggerRect.top, right: window.innerWidth - triggerRect.right });
		setIsOpen(true);
	};

	/*
	 * Closing is the player's, three ways: Escape, a press outside, or the
	 * trigger again. Escape returns focus to the trigger — a panel that closes
	 * and leaves focus on `body` strands a keyboard player at the top of the
	 * document. `pointerdown` rather than `click` so a press that begins outside
	 * closes immediately rather than waiting for a release the player may make
	 * somewhere else entirely.
	 */
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const closeOnOutsidePointer = (pointerEvent: PointerEvent): void => {
			const target = pointerEvent.target;

			if (target instanceof Node && containerElementRef.current?.contains(target) !== true) {
				setIsOpen(false);
			}
		};

		const closeOnEscape = (keyboardEvent: KeyboardEvent): void => {
			if (keyboardEvent.key === 'Escape') {
				setIsOpen(false);
				triggerElementRef.current?.focus();
			}
		};

		const closeOnResize = (): void => {
			setIsOpen(false);
		};

		document.addEventListener('pointerdown', closeOnOutsidePointer);
		document.addEventListener('keydown', closeOnEscape);
		window.addEventListener('resize', closeOnResize);

		return () => {
			document.removeEventListener('pointerdown', closeOnOutsidePointer);
			document.removeEventListener('keydown', closeOnEscape);
			window.removeEventListener('resize', closeOnResize);
		};
	}, [isOpen]);

	return (
		<div className="settings-control" ref={containerElementRef}>
			<button
				aria-controls="settings-panel"
				aria-expanded={isOpen}
				// State first, then what the button does — the shape the row's other
				// triggers use, and what a screen reader should hear first. The only
				// state the gear has to tell is the one it took over from the speaker.
				aria-label={isSoundMuted ? 'Settings — sound off' : 'Settings'}
				className="settings-control__trigger"
				onClick={(pressEvent) => {
					if (isOpen) {
						setIsOpen(false);
					} else {
						openPanel(pressEvent.currentTarget);
					}
				}}
				ref={triggerElementRef}
				type="button"
			>
				<GearIcon />
				{isSoundMuted ? <MutedMark /> : undefined}
				<span className="settings-control__label">Settings</span>
				<DisclosureCaret />
			</button>
			{isOpen ? (
				<div
					aria-label="Settings"
					className="settings-control__panel"
					id="settings-panel"
					role="group"
					style={{ bottom: `${String(panelAnchor.bottom + 8)}px`, right: `${String(panelAnchor.right)}px` }}
				>
					{children}
				</div>
			) : undefined}
		</div>
	);
};

/**
 * The gear — the one glyph every platform reads as "settings".
 *
 * @returns The icon
 */
const GearIcon = () => (
	<svg
		aria-hidden="true"
		className="settings-control__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<circle cx="12" cy="12" r="3" />
		<path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
	</svg>
);

/**
 * The struck speaker the gear wears while sound is off — the state the old
 * speaker trigger showed without opening anything, kept at the corner.
 *
 * @returns The mark
 */
const MutedMark = () => (
	<svg
		aria-hidden="true"
		className="settings-control__muted-mark"
		fill="none"
		focusable="false"
		height="9"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={2.2}
		viewBox="0 0 24 24"
		width="9"
	>
		<path d="M11 5 6 9H3v6h3l5 4z" />
		<path d="M4 20 20 4" />
	</svg>
);

/**
 * The caret that says "this opens something" — the visible affordance a
 * long-press would not have had (D130's argument, unchanged).
 *
 * @returns The caret
 */
const DisclosureCaret = () => (
	<svg
		aria-hidden="true"
		className="settings-control__caret"
		fill="none"
		focusable="false"
		height="10"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={2}
		viewBox="0 0 12 12"
		width="10"
	>
		<path d="m3 4.5 3 3 3-3" />
	</svg>
);
